// Copyright (c) Microsoft. All rights reserved.

import { Constants } from '../../Constants';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert, updateTokenUsage } from '../../redux/features/app/appSlice';
import { ChatState } from '../../redux/features/conversations/ChatState';
import { Conversations } from '../../redux/features/conversations/ConversationsState';
import {
    addConversation,
    addMessageToConversationFromUser,
    setConversations,
    setSelectedConversation,
    updateBotResponseStatus,
    updateMessageProperty,
} from '../../redux/features/conversations/conversationsSlice';
import { AlertType } from '../models/AlertType';
import { AuthorRoles, ChatMessageType, IChatMessage, UserFeedback, UserFeedbackSource } from '../models/ChatMessage';
import { IChatSession, ICreateChatSessionResponse } from '../models/ChatSession';
import { ResponseStyle } from '../models/UserPreferences';
import { IChatUser } from '../models/ChatUser';
import { TokenUsage } from '../models/TokenUsage';
import { IAsk, IAskImage, IAskVariables } from '../semantic-kernel/model/Ask';
import { NotFoundError } from '../services/BaseService';
import { ChatService } from '../services/ChatService';

import { useTranslation } from 'react-i18next';
import botIcon1 from '../../assets/bot-icons/bot-icon-1.png';
import botIcon2 from '../../assets/bot-icons/bot-icon-2.png';
import botIcon3 from '../../assets/bot-icons/bot-icon-3.png';
import botIcon4 from '../../assets/bot-icons/bot-icon-4.png';
import botIcon5 from '../../assets/bot-icons/bot-icon-5.png';
import { HostContext } from '../../embed-script/HostContext';
import { useAuth } from 'react-oidc-context';
import { useMemo } from 'react';

export interface GetResponseOptions {
    messageType: ChatMessageType;
    value: string;
    chatId: string;
    contextVariables?: IAskVariables[];
    hostContext?: HostContext;
    /** Optional pasted screenshot — issue #1504. Sent to backend, never persisted. */
    image?: IAskImage;
    /**
     * Per-request correlation id forwarded to the backend so status events can be
     * filtered against the active run on the client (#1564). Generated by ChatRoom
     * before this hook is called and dispatched into Redux state at the same time
     * so the SignalR handler has it available when status events start arriving.
     */
    runId?: string;
}

export const useChat = () => {
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const token = auth.user?.access_token;

    const chatService = useMemo(() => new ChatService(token ?? ''), [token]);

    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, aiProviderSelection, agentOverrides } = useAppSelector((state: RootState) => state.app);
    const providerSelectionEnabled = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.EnableAiProviderSelection,
    );
    const { t } = useTranslation();

    const botProfilePictures: string[] = [botIcon1, botIcon2, botIcon3, botIcon4, botIcon5];

    const userId = activeUserInfo?.id ?? '';
    const fullName = activeUserInfo?.username ?? '';
    const emailAddress = activeUserInfo?.email ?? '';
    const loggedInUser: IChatUser = {
        id: userId,
        fullName,
        emailAddress,
        photo: undefined, // TODO: [Issue #45] Make call to Graph /me endpoint to load photo
        online: true,
    };
    const hostContextState = useAppSelector((state: RootState) => state.hostContext.hostContext);

    const getChatUserById = (id: string, chatId: string, users: IChatUser[]) => {
        if (id === `${chatId}-bot` || id.toLocaleLowerCase() === 'bot') return Constants.bot.profile;
        return users.find((user) => user.id === id);
    };

    /**
     * Submit thumbs-up/down feedback on a bot message. Errors propagate so the caller can
     * react — notably ConflictError (#1566), which the server returns after retry exhaustion
     * on optimistic-concurrency conflicts; the caller is expected to roll back the optimistic
     * UI flip and surface a "refresh and try again" toast.
     */
    const userFeedbackOnMessage = async (
        chatId: string,
        messageId: string,
        feedback: UserFeedback,
        source?: UserFeedbackSource,
    ) => {
        await chatService.updateUserFeedbackAsync(chatId, messageId, feedback, undefined, source);
    };

    /**
     * Restyle (#1551): rewrite an existing bot message in the opposite verbosity. The new content
     * arrives via SignalR while this promise is pending; the resolve only signals "workflow done".
     * Errors (e.g. ConflictError when sources are gone) propagate so the caller can localize them.
     */
    const restyleMessage = async (chatId: string, messageId: string, targetStyle: ResponseStyle) => {
        await chatService.restyleMessageAsync(chatId, messageId, targetStyle);
    };

    const createChat = async (hostContext?: HostContext) => {
        try {
            await chatService.createChatAsync(hostContext).then((result: ICreateChatSessionResponse) => {
                const newChat: ChatState = {
                    id: result.chatSession.id,
                    messages: [result.initialBotMessage],
                    enabledHostedPlugins: result.chatSession.enabledPlugins,
                    users: [loggedInUser],
                    botProfilePicture: getBotProfilePicture(Object.keys(conversations).length),
                    input: '',
                    botResponseStatus: undefined,
                    userDataLoaded: false,
                    disabled: false,
                    copiedText: '',
                    botResponseStatusHistory: [],
                    hostContext: hostContext,
                };

                dispatch(addConversation(newChat));
                dispatch(setSelectedConversation(newChat.id));
                return newChat.id;
            });
        } catch (e: any) {
            const errorMessage = `Unable to create new chat. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const getResponse = async ({
        messageType,
        value,
        chatId,
        contextVariables,
        hostContext,
        image,
        runId,
    }: GetResponseOptions) => {
        const chatInput: IChatMessage = {
            chatId: chatId,
            timestamp: new Date().getTime(),
            userId: activeUserInfo?.id as string,
            userName: activeUserInfo?.username as string,
            content: value,
            type: messageType,
            authorRole: AuthorRoles.User,
            // Local-only preview so the user sees their pasted screenshot in the bubble
            // immediately. Never persisted; gone after a page refresh.
            localImagePreview: image ? `data:${image.mimeType};base64,${image.base64}` : undefined,
        };

        dispatch(addMessageToConversationFromUser({ message: chatInput, chatId: chatId }));

        const ask: IAsk = {
            input: value,
            variables: [
                {
                    key: 'chatId',
                    value: chatId,
                },
                {
                    key: 'messageType',
                    value: messageType.toString(),
                },
            ],
            hostContext: hostContext,
            image: image,
            runId: runId,
        };

        if (contextVariables && ask.variables) {
            ask.variables.push(...contextVariables);
        }

        try {
            // Only forward the panel-driven provider/model selection and per-agent overrides while the
            // feature is enabled. If ops flips EnableAiProviderSelection off to roll the experiment back, a
            // value still sitting in localStorage must not keep reaching the server — its server gate is
            // internal-email only, not flag-gated — and the panel is no longer rendered to clear it. The
            // persisted value is left intact so re-enabling restores the user's choice. (#2440)
            const providerSelectionToSend = providerSelectionEnabled ? aiProviderSelection : undefined;
            const agentOverridesToSend = providerSelectionEnabled ? agentOverrides : undefined;

            // Log the AI provider selection and agent overrides being used
            if (providerSelectionToSend?.providerId || providerSelectionToSend?.modelId) {
                console.log('Sending chat request with AI provider selection:', {
                    providerId: providerSelectionToSend.providerId,
                    modelId: providerSelectionToSend.modelId,
                    chatId,
                });
            }
            if (agentOverridesToSend && Object.keys(agentOverridesToSend).length > 0) {
                console.log('Sending chat request with agent overrides:', agentOverridesToSend);
            }

            const askResult = await chatService
                .getBotResponseAsync(ask, hostContext, providerSelectionToSend, agentOverridesToSend)
                .catch((e: unknown) => {
                    throw e;
                });

            // Cancelled requests (#1554) return 204 with an empty body — the cancel thunk has
            // already restored the UI to edit mode, so we bail before treating it as an error.
            // The IAskResult type says variables is required, but a 204 maps to {} via
            // BaseService — Array.isArray distinguishes the real result from the cancel sentinel.
            if (!Array.isArray(askResult.variables)) {
                return;
            }

            // Update token usage of current session
            const responseTokenUsage = askResult.variables.find((v) => v.key === 'tokenUsage')?.value;
            if (responseTokenUsage) dispatch(updateTokenUsage(JSON.parse(responseTokenUsage) as TokenUsage));
            if (askResult.message != null) {
                dispatch(
                    updateMessageProperty({
                        chatId: chatId,
                        messageIdOrIndex: askResult.message.id as string,
                        property: 'followUpAsks',
                        value: askResult.message.followUpAsks,
                        frontLoad: true,
                    }),
                );
            }
        } catch (e: any) {
            dispatch(updateBotResponseStatus({ chatId, status: undefined }));

            const errorDetails = getErrorDetails(e);
            if (errorDetails.includes('Failed to process plan')) {
                // Error should already be reflected in bot response message. Skip alert.
                return;
            }
            if (errorDetails.includes('ChatTooManyRequestsException')) {
                const errorMessage = t('alerts.toobusy');
                dispatch(addAlert({ message: errorMessage, type: AlertType.Warning }));
                return;
            }
            const errorMessage = `Unable to generate bot response. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    /**
     * A 404 on the messages means the session was deleted after the lookup (e.g. in another tab), so it
     * is looked up once more; a second 404 resolves to null so the caller starts a new chat.
     */
    const getLastChatWithMessagesAsync = async (
        hostContext?: HostContext,
        retriesLeft = 1,
    ): Promise<{ session: IChatSession; messages: IChatMessage[] } | null> => {
        const session = await chatService.getLastChatSessionAsync(hostContext);
        if (session === null) {
            return null;
        }
        // A rate-limited response resolves to an empty object rather than throwing.
        if (!session.id) {
            throw new Error('The last chat session response has no id.');
        }
        try {
            return { session, messages: await chatService.getChatMessagesAsync(session.id, 0, 100, hostContext) };
        } catch (e: unknown) {
            if (!(e instanceof NotFoundError)) {
                throw e;
            }
            return retriesLeft > 0 ? getLastChatWithMessagesAsync(hostContext, retriesLeft - 1) : null;
        }
    };

    const loadChats = async (hostContext?: HostContext) => {
        try {
            const lastChat = await getLastChatWithMessagesAsync(hostContext);
            if (lastChat !== null) {
                const { session: lastChatSession, messages: chatMessages } = lastChat;
                const loadedConversations: Conversations = {};
                loadedConversations[lastChatSession.id] = {
                    id: lastChatSession.id,
                    users: [loggedInUser],
                    messages: chatMessages,
                    enabledHostedPlugins: lastChatSession.enabledPlugins,
                    botProfilePicture: getBotProfilePicture(Object.keys(loadedConversations).length),
                    input: '',
                    botResponseStatus: undefined,
                    userDataLoaded: false,
                    disabled: false,
                    copiedText: '',
                    botResponseStatusHistory: [],
                    hostContext: hostContext,
                };

                dispatch(setConversations(loadedConversations));
                dispatch(setSelectedConversation(lastChatSession.id));
                // If there are no non-hidden chats, create a new chat
                const nonHiddenChats = Object.values(loadedConversations);
                if (nonHiddenChats.length === 0) {
                    await createChat(hostContext);
                } else {
                    dispatch(setSelectedConversation(nonHiddenChats[0].id));
                }
            } else {
                // No usable last chat: none exists, or it kept returning 404
                dispatch(setConversations({}));
                await createChat(hostContext);
            }

            return true;
        } catch (e: any) {
            const errorMessage = `Unable to load chats. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return false;
        }
    };

    const getBotProfilePicture = (index: number): string => {
        return botProfilePictures[index % botProfilePictures.length];
    };

    const getServiceInfo = async (hostContext?: HostContext) => {
        try {
            return await chatService.getServiceInfoAsync(hostContext);
        } catch (e: any) {
            const errorMessage = `Error getting service options. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return undefined;
        }
    };

    const shouldShowCsat = async () => {
        try {
            return await chatService.shouldShowCsatAsync(hostContextState, selectedId);
        } catch (e) {
            console.error('Failed to check CSAT status:', e);
            return false;
        }
    };

    const selectedId = useAppSelector((state: RootState) => state.conversations.selectedId);

    const submitCsatRatings = async (
        satisfaction: number,
        comment?: string,
        source: 'modal' | 'inline' = 'modal',
    ): Promise<boolean> => {
        if (!selectedId) return false;

        try {
            await chatService.submitCsatAsync(selectedId, satisfaction, comment, hostContextState, source);
            return true;
        } catch (e) {
            console.error('Failed to submit CSAT ratings:', e);
            const errorMessage = t('csat.submitError');
            dispatch(addAlert({ message: errorMessage, type: AlertType.Warning }));
            return false;
        }
    };

    const declineCsat = async () => {
        try {
            // Pass the selected chat so the server persists the "responded" flag on the flagged
            // message (#1641) — without it, dismissing then reloading re-shows the widget.
            await chatService.declineCsatAsync(selectedId, hostContextState);
        } catch (e) {
            console.error('Failed to decline CSAT:', e);
            const errorMessage = t('csat.submitError');
            dispatch(addAlert({ message: errorMessage, type: AlertType.Warning }));
        }
    };

    return {
        getChatUserById,
        createChat,
        loadChats,
        getResponse,
        getServiceInfo,
        userFeedbackOnMessage,
        restyleMessage,
        shouldShowCsat,
        submitCsatRatings,
        declineCsat,
    };
};
export function getErrorDetails(e: any) {
    return e instanceof Error ? e.message : String(e);
}

export function getFriendlyChatName(convo: ChatState): string {
    const messages = convo.messages;

    const firstUserMessage = messages.find((message) => message.authorRole !== AuthorRoles.Bot);

    // If the chat title is the default Copilot timestamp, use the first user message as the title.
    // If no user messages exist, use 'New Chat' as the title.
    const friendlyTitle = firstUserMessage?.content ?? 'New Chat';

    // Truncate the title if it is too long
    return friendlyTitle.length > 60 ? friendlyTitle.substring(0, 60) + '...' : friendlyTitle;
}
